import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { mentorshipAPI, usersAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import { Switch } from '../components/ui/switch';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Users, Sparkles, MessageCircle, Building, GraduationCap, Search,
  Loader2, Target, BookOpen, TrendingUp, Filter, ArrowRight,
  FileText, Upload, CheckCircle, AlertCircle, Star, Download, Wand2, Eye, EyeOff
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import usePageTitle from '../hooks/usePageTitle';

const INDUSTRIES = [
  'All Industries', 'Technology', 'Healthcare', 'Finance', 'Education',
  'Marketing', 'Law', 'Real Estate', 'Consulting', 'Government', 'Nonprofit'
];

const Mentorship = () => {
  usePageTitle('Mentorship');
  const { user } = useAuth();
  const [mentors, setMentors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [industryFilter, setIndustryFilter] = useState('All Industries');
  const [activeTab, setActiveTab] = useState('discover');

  // Resume state
  const [resumeFile, setResumeFile] = useState(null);
  const [reviewResult, setReviewResult] = useState(null);
  const [reformatResult, setReformatResult] = useState(null);
  const [generateResult, setGenerateResult] = useState(null);
  const [resumeLoading, setResumeLoading] = useState(false);
  const [resumeAction, setResumeAction] = useState(null); // 'review' | 'reformat' | 'generate'
  const [mentoringVisible, setMentoringVisible] = useState(!user?.mentoring_opted_out);

  const handleMentoringToggle = async (visible) => {
    setMentoringVisible(visible);
    try {
      await usersAPI.updateProfile({ mentoring_opted_out: !visible });
      toast.success(visible ? 'You are now discoverable in mentorship' : 'You have opted out of mentorship');
    } catch (error) {
      setMentoringVisible(!visible);
      toast.error('Failed to update mentorship visibility');
    }
  };

  useEffect(() => {
    fetchMentors();
  }, []);

  const fetchMentors = async () => {
    try {
      const res = await mentorshipAPI.getMatches();
      setMentors(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('Failed to fetch mentors:', error);
    } finally {
      setLoading(false);
    }
  };

  const profileCompletion = user?.profile_completion || 0;

  const filteredMentors = mentors.filter(m => {
    if (search && !m.mentor_name?.toLowerCase().includes(search.toLowerCase()) &&
        !m.mentor_title?.toLowerCase().includes(search.toLowerCase()) &&
        !m.mentor_company?.toLowerCase().includes(search.toLowerCase())) return false;
    if (industryFilter !== 'All Industries' && !m.shared_industry) return false;
    return true;
  });

  const handleResumeUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'application/pdf') { toast.error('Please upload a PDF file'); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error('Max file size is 10MB'); return; }
    setResumeFile(file);
    setReviewResult(null);
    setReformatResult(null);
  };

  const handleReviewResume = async () => {
    if (!resumeFile) { toast.error('Please upload a resume first'); return; }
    setResumeLoading(true);
    setResumeAction('review');
    try {
      const res = await mentorshipAPI.reviewResume(resumeFile);
      setReviewResult(res.data);
      toast.success('Resume reviewed!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to review resume');
    } finally {
      setResumeLoading(false);
      setResumeAction(null);
    }
  };

  const handleReformatResume = async () => {
    if (!resumeFile) { toast.error('Please upload a resume first'); return; }
    setResumeLoading(true);
    setResumeAction('reformat');
    try {
      const res = await mentorshipAPI.reformatResume(resumeFile);
      setReformatResult(res.data);
      toast.success('Resume reformatted!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reformat resume');
    } finally {
      setResumeLoading(false);
      setResumeAction(null);
    }
  };

  const handleGenerateResume = async () => {
    setResumeLoading(true);
    setResumeAction('generate');
    try {
      const res = await mentorshipAPI.generateResume();
      setGenerateResult(res.data);
      toast.success('Resume generated from your profile!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate resume');
    } finally {
      setResumeLoading(false);
      setResumeAction(null);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-slate-gray" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="font-heading font-extrabold text-3xl text-dark-slate mb-2">Mentorship</h1>
          <p className="text-slate-gray">AI-powered mentor matching and resume tools</p>
        </div>

        {/* Mentoring visibility toggle */}
        <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-ambient mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {mentoringVisible ? (
                <Eye className="w-5 h-5 text-emerald-500" />
              ) : (
                <EyeOff className="w-5 h-5 text-slate-400" />
              )}
              <div>
                <p className="text-sm font-semibold text-dark-slate">Discoverable in Mentorship</p>
                <p className="text-xs text-slate-gray">When off, you won't appear in others' mentor recommendations</p>
              </div>
            </div>
            <Switch
              checked={mentoringVisible}
              onCheckedChange={handleMentoringToggle}
              className="data-[state=checked]:bg-emerald-500"
            />
          </div>
        </div>

        {/* Tabs */}
        <div className="flex bg-slate-100 rounded-full p-1 mb-6">
          <button onClick={() => setActiveTab('discover')}
            className={`flex-1 py-2.5 rounded-full text-sm font-medium transition-colors ${
              activeTab === 'discover' ? 'bg-white text-dark-slate shadow-sm' : 'text-slate-gray'
            }`}>
            <Sparkles className="w-4 h-4 inline mr-1.5" />Discover
          </button>
          <button onClick={() => setActiveTab('resume')}
            className={`flex-1 py-2.5 rounded-full text-sm font-medium transition-colors ${
              activeTab === 'resume' ? 'bg-white text-dark-slate shadow-sm' : 'text-slate-gray'
            }`}>
            <FileText className="w-4 h-4 inline mr-1.5" />Resume AI
          </button>
          <button onClick={() => setActiveTab('goals')}
            className={`flex-1 py-2.5 rounded-full text-sm font-medium transition-colors ${
              activeTab === 'goals' ? 'bg-white text-dark-slate shadow-sm' : 'text-slate-gray'
            }`}>
            <Target className="w-4 h-4 inline mr-1.5" />Goals
          </button>
        </div>

        {activeTab === 'discover' ? (
          <>
            {/* Profile completion prompt */}
            {profileCompletion < 80 && (
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl p-5 border border-amber-100 mb-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-dark-slate flex items-center">
                    <TrendingUp className="w-5 h-5 mr-2 text-amber-600" />Improve Your Matches
                  </h3>
                  <span className="text-amber-700 font-bold">{profileCompletion}%</span>
                </div>
                <Progress value={profileCompletion} className="h-2 mb-3" />
                <p className="text-sm text-slate-gray mb-3">Complete your profile to get more accurate mentor recommendations.</p>
                <Link to="/profile">
                  <Button size="sm" className="rounded-full bg-amber-600 hover:bg-amber-700 text-white">
                    Complete Profile <ArrowRight className="w-3 h-3 ml-1" />
                  </Button>
                </Link>
              </div>
            )}

            {/* Search & Filter */}
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-gray" />
                <Input placeholder="Search mentors by name, title, company..." value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 h-11 rounded-xl bg-white border-slate-200" />
              </div>
              <Select value={industryFilter} onValueChange={setIndustryFilter}>
                <SelectTrigger className="h-11 w-[180px] rounded-xl bg-white border-slate-200">
                  <Filter className="w-4 h-4 mr-2" /><SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {INDUSTRIES.map(ind => <SelectItem key={ind} value={ind}>{ind}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Mentor List */}
            {filteredMentors.length === 0 ? (
              <div className="bg-white rounded-3xl p-12 text-center border border-slate-100 shadow-ambient">
                <Users className="w-16 h-16 text-slate-200 mx-auto mb-4" />
                <h3 className="font-heading font-bold text-xl text-dark-slate mb-2">
                  {mentors.length === 0 ? 'No matches yet' : 'No mentors match your filters'}
                </h3>
                <p className="text-slate-gray mb-4">
                  {mentors.length === 0 ? 'Complete your profile to get mentor recommendations' : 'Try adjusting your search or filters'}
                </p>
                {mentors.length === 0 && (
                  <Link to="/profile">
                    <Button className="rounded-full bg-dark-slate">Update Profile</Button>
                  </Link>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {filteredMentors.map((mentor) => (
                  <div key={mentor.mentor_id}
                    className="bg-white rounded-2xl p-5 border border-slate-100 shadow-ambient hover:shadow-hover transition-shadow">
                    <div className="flex flex-col sm:flex-row gap-4">
                      <Avatar className="w-16 h-16 sm:w-20 sm:h-20 flex-shrink-0">
                        <AvatarImage src={mentor.mentor_photo} />
                        <AvatarFallback className="bg-mint-fresh text-dark-slate text-2xl">
                          {mentor.mentor_name?.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-heading font-bold text-lg text-dark-slate">{mentor.mentor_name}</h3>
                            {mentor.mentor_title && (
                              <p className="text-slate-gray text-sm">
                                {mentor.mentor_title}{mentor.mentor_company && ` at ${mentor.mentor_company}`}
                              </p>
                            )}
                          </div>
                          <div className="bg-gradient-to-br from-amber-100 to-amber-200 rounded-full px-3 py-1 flex items-center flex-shrink-0">
                            <Sparkles className="w-3.5 h-3.5 text-amber-600 mr-1" />
                            <span className="font-bold text-amber-800 text-sm">{mentor.compatibility_score}%</span>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2 mt-3">
                          {mentor.same_institution && (
                            <Badge variant="secondary" className="bg-emerald-50 text-emerald-700 text-xs">
                              <GraduationCap className="w-3 h-3 mr-1" />Same School
                            </Badge>
                          )}
                          {mentor.shared_industry && (
                            <Badge variant="secondary" className="bg-blue-50 text-blue-700 text-xs">
                              <Building className="w-3 h-3 mr-1" />Same Industry
                            </Badge>
                          )}
                          {mentor.shared_interests?.slice(0, 3).map((interest, i) => (
                            <Badge key={i} variant="outline" className="text-xs">{interest}</Badge>
                          ))}
                        </div>
                        <div className="flex gap-3 mt-4">
                          <Link to={`/chat/${mentor.mentor_id}`}>
                            <Button size="sm" className="rounded-full bg-dark-slate">
                              <MessageCircle className="w-4 h-4 mr-2" />Request Mentorship
                            </Button>
                          </Link>
                          <Link to={`/profile/${mentor.mentor_id}`}>
                            <Button size="sm" variant="outline" className="rounded-full">View Profile</Button>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : activeTab === 'resume' ? (
          /* Resume AI Tab */
          <div className="space-y-6">
            {/* Upload Section */}
            <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-ambient">
              <h2 className="font-heading font-bold text-xl text-dark-slate mb-2 flex items-center">
                <FileText className="w-5 h-5 mr-2 text-emerald-600" />Resume AI Tools
              </h2>
              <p className="text-slate-gray text-sm mb-5">Upload your resume for AI-powered review, or generate one from your profile.</p>

              <div className="flex flex-col sm:flex-row gap-4 items-start">
                <label className="flex-1 cursor-pointer">
                  <div className={`border-2 border-dashed rounded-2xl p-6 text-center transition-colors ${
                    resumeFile ? 'border-emerald-300 bg-emerald-50' : 'border-slate-300 hover:border-slate-400 bg-slate-50'
                  }`}>
                    {resumeFile ? (
                      <div className="flex items-center justify-center gap-2">
                        <CheckCircle className="w-5 h-5 text-emerald-600" />
                        <span className="font-medium text-dark-slate">{resumeFile.name}</span>
                        <span className="text-xs text-slate-gray">({(resumeFile.size / 1024).toFixed(0)} KB)</span>
                      </div>
                    ) : (
                      <>
                        <Upload className="w-8 h-8 text-slate-gray mx-auto mb-2" />
                        <p className="text-sm text-dark-slate font-medium">Upload Resume (PDF)</p>
                        <p className="text-xs text-slate-gray mt-1">Max 10MB</p>
                      </>
                    )}
                  </div>
                  <input type="file" accept=".pdf" className="hidden" onChange={handleResumeUpload} />
                </label>
              </div>

              {/* Action buttons */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-5">
                <Button onClick={handleReviewResume} disabled={!resumeFile || resumeLoading}
                  className="h-12 bg-dark-slate text-white rounded-xl">
                  {resumeLoading && resumeAction === 'review' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                  Review Resume
                </Button>
                <Button onClick={handleReformatResume} disabled={!resumeFile || resumeLoading}
                  variant="outline" className="h-12 rounded-xl">
                  {resumeLoading && resumeAction === 'reformat' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
                  Reformat for ATS
                </Button>
                <Button onClick={handleGenerateResume} disabled={resumeLoading}
                  variant="outline" className="h-12 rounded-xl">
                  {resumeLoading && resumeAction === 'generate' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Star className="w-4 h-4 mr-2" />}
                  Generate from Profile
                </Button>
              </div>
            </div>

            {/* Review Results */}
            {reviewResult && (
              <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-ambient">
                <h3 className="font-heading font-bold text-lg text-dark-slate mb-4 flex items-center">
                  <CheckCircle className="w-5 h-5 mr-2 text-emerald-600" />Resume Review
                </h3>

                <div className="grid grid-cols-2 gap-4 mb-5">
                  <div className="bg-slate-50 rounded-xl p-4 text-center">
                    <p className="text-3xl font-bold text-dark-slate">{reviewResult.overall_score || 0}</p>
                    <p className="text-xs text-slate-gray mt-1">Overall Score</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4 text-center">
                    <p className="text-3xl font-bold text-emerald-600">{reviewResult.ats_score || 0}</p>
                    <p className="text-xs text-slate-gray mt-1">ATS Score</p>
                  </div>
                </div>

                {reviewResult.summary && (
                  <p className="text-sm text-slate-gray mb-4 bg-slate-50 rounded-xl p-3">{reviewResult.summary}</p>
                )}

                {reviewResult.strengths?.length > 0 && (
                  <div className="mb-4">
                    <h4 className="font-semibold text-dark-slate text-sm mb-2 flex items-center">
                      <CheckCircle className="w-4 h-4 mr-1.5 text-emerald-600" />Strengths
                    </h4>
                    <ul className="space-y-1">
                      {reviewResult.strengths.map((s, i) => (
                        <li key={i} className="text-sm text-slate-gray flex items-start">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-2 mr-2 flex-shrink-0" />{s}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {reviewResult.improvements?.length > 0 && (
                  <div className="mb-4">
                    <h4 className="font-semibold text-dark-slate text-sm mb-2 flex items-center">
                      <AlertCircle className="w-4 h-4 mr-1.5 text-amber-600" />Areas for Improvement
                    </h4>
                    <ul className="space-y-1">
                      {reviewResult.improvements.map((s, i) => (
                        <li key={i} className="text-sm text-slate-gray flex items-start">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-2 mr-2 flex-shrink-0" />{s}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {reviewResult.formatting_tips?.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-dark-slate text-sm mb-2">Formatting Tips</h4>
                    <ul className="space-y-1">
                      {reviewResult.formatting_tips.map((s, i) => (
                        <li key={i} className="text-sm text-slate-gray flex items-start">
                          <span className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2 mr-2 flex-shrink-0" />{s}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Reformat Results */}
            {reformatResult && (
              <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-ambient">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-heading font-bold text-lg text-dark-slate flex items-center">
                    <Wand2 className="w-5 h-5 mr-2 text-indigo-600" />ATS-Optimized Resume
                  </h3>
                  <Button size="sm" variant="outline" className="rounded-full text-xs"
                    onClick={() => copyToClipboard(reformatResult.formatted_resume)}>
                    <Download className="w-3 h-3 mr-1" />Copy
                  </Button>
                </div>
                <pre className="bg-slate-50 rounded-xl p-4 text-sm text-dark-slate whitespace-pre-wrap font-sans max-h-96 overflow-y-auto">
                  {reformatResult.formatted_resume}
                </pre>
                {reformatResult.changes_made?.length > 0 && (
                  <div className="mt-4">
                    <h4 className="font-semibold text-dark-slate text-sm mb-2">Changes Made</h4>
                    <div className="flex flex-wrap gap-2">
                      {reformatResult.changes_made.map((c, i) => (
                        <Badge key={i} variant="secondary" className="bg-indigo-50 text-indigo-700 text-xs">{c}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Generate Results */}
            {generateResult && (
              <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-ambient">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-heading font-bold text-lg text-dark-slate flex items-center">
                    <Star className="w-5 h-5 mr-2 text-amber-600" />Generated Resume
                  </h3>
                  <Button size="sm" variant="outline" className="rounded-full text-xs"
                    onClick={() => copyToClipboard(generateResult.resume_text)}>
                    <Download className="w-3 h-3 mr-1" />Copy
                  </Button>
                </div>
                <pre className="bg-slate-50 rounded-xl p-4 text-sm text-dark-slate whitespace-pre-wrap font-sans max-h-96 overflow-y-auto">
                  {generateResult.resume_text}
                </pre>
                {generateResult.suggestions?.length > 0 && (
                  <div className="mt-4">
                    <h4 className="font-semibold text-dark-slate text-sm mb-2">Profile Improvement Suggestions</h4>
                    <ul className="space-y-1">
                      {generateResult.suggestions.map((s, i) => (
                        <li key={i} className="text-sm text-slate-gray flex items-start">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-2 mr-2 flex-shrink-0" />{s}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          /* Goals Tab */
          <div className="space-y-6">
            <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-ambient">
              <h2 className="font-heading font-bold text-xl text-dark-slate mb-4 flex items-center">
                <Target className="w-5 h-5 mr-2 text-emerald-600" />Your Mentorship Goals
              </h2>
              <p className="text-slate-gray text-sm mb-6">
                Setting clear goals helps us match you with the right mentors.
              </p>
              <div className="space-y-4">
                {[
                  { icon: TrendingUp, label: 'Career Growth', desc: 'Get guidance on advancing in your career' },
                  { icon: Building, label: 'Industry Transition', desc: 'Navigate switching to a new industry' },
                  { icon: BookOpen, label: 'Skill Development', desc: 'Learn new skills from experienced professionals' },
                  { icon: Users, label: 'Networking', desc: 'Expand your professional connections' },
                ].map((goal, i) => (
                  <button key={i} onClick={() => toast.success(`"${goal.label}" goal saved!`)}
                    className="w-full flex items-center space-x-4 p-4 rounded-xl bg-slate-50 hover:bg-mint-fresh/20 transition-colors text-left">
                    <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shadow-sm">
                      <goal.icon className="w-5 h-5 text-dark-slate" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-dark-slate">{goal.label}</p>
                      <p className="text-xs text-slate-gray">{goal.desc}</p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-slate-gray" />
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-gradient-to-br from-mint-fresh/30 to-emerald-50 rounded-3xl p-6 border border-emerald-100">
              <h3 className="font-semibold text-dark-slate mb-2">How it works</h3>
              <div className="space-y-3">
                {[
                  { step: '1', text: 'Set your mentorship goals and complete your profile' },
                  { step: '2', text: 'Our AI matches you with alumni who can help' },
                  { step: '3', text: 'Send a connection request to start the conversation' },
                  { step: '4', text: 'Schedule sessions and track your progress' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start space-x-3">
                    <div className="w-7 h-7 rounded-full bg-dark-slate text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                      {item.step}
                    </div>
                    <p className="text-sm text-slate-gray pt-1">{item.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Mentorship;
